import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Typography,
  Box,
  Button,
  Rating,
  Chip,
  Paper,
  Divider,
  TextField,
  Alert,
  Snackbar,
} from '@mui/material';
import { ShoppingCart, Check } from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { productService, reviewService } from '../services';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import Loading from '../components/Loading';

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { isAuthenticated } = useAuth();
  
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [review, setReview] = useState({ rating: 5, comment: '' });

  const API_URL = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000';

  useEffect(() => {
    fetchProduct();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchProduct = async () => {
    try {
      setLoading(true);
      const response = await productService.getOne(id);
      setProduct(response.data);
    } catch (error) {
      console.error('Error fetching product:', error);
      setSnackbar({ open: true, message: 'Product not found', severity: 'error' });
      setTimeout(() => navigate('/products'), 2000);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = async () => {
    try {
      // For guest users, pass product details so they can be stored in localStorage
      const productDetails = !isAuthenticated ? {
        name: product.name,
        price: product.price,
        image: product.image,
        sku: product.sku
      } : null;
      
      await addToCart(product.id, quantity, productDetails);
      setSnackbar({ open: true, message: 'Product added to cart!', severity: 'success' });
    } catch (error) {
      setSnackbar({ 
        open: true, 
        message: error.response?.data?.message || 'Failed to add to cart', 
        severity: 'error' 
      });
    }
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    
    if (!isAuthenticated) {
      setSnackbar({ open: true, message: 'Please login to submit a review', severity: 'warning' });
      return;
    }

    try {
      await reviewService.add({ product_id: product.id, ...review });
      setSnackbar({ open: true, message: 'Review submitted successfully!', severity: 'success' });
      setReview({ rating: 5, comment: '' });
      fetchProduct(); // Refresh to show new review
    } catch (error) {
      setSnackbar({ 
        open: true, 
        message: error.response?.data?.message || 'Failed to submit review', 
        severity: 'error' 
      });
    }
  };

  if (loading) return <Loading />;
  if (!product) return null;

  const imageUrl = product.image ? `${API_URL}${product.image}` : '/placeholder-product.jpg';

  return (
    <>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Grid container spacing={4}>
          {/* Product Image */}
          <Grid item xs={12} md={6}>
            <Box
              component="img"
              src={imageUrl}
              alt={product.name}
              sx={{
                width: '100%',
                height: 'auto',
                maxHeight: 500,
                objectFit: 'contain',
                borderRadius: 2,
              }}
              onError={(e) => e.target.src = '/placeholder-product.jpg'}
            />
          </Grid>

          {/* Product Info */}
          <Grid item xs={12} md={6}>
            <Box>
              <Typography variant="overline" color="text.secondary">
                {product.category_name}
              </Typography>
              
              <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
                {product.name}
              </Typography>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Rating value={product.average_rating || 0} precision={0.5} readOnly />
                <Typography variant="body2" color="text.secondary">
                  ({product.review_count} reviews)
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
                {product.is_new && <Chip label="NEW" color="success" size="small" />}
                {product.is_featured && <Chip label="FEATURED" color="primary" size="small" />}
                {product.stock_quantity > 0 ? (
                  <Chip label="IN STOCK" color="success" variant="outlined" icon={<Check />} />
                ) : (
                  <Chip label="OUT OF STOCK" color="error" variant="outlined" />
                )}
              </Box>

              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="h4" color="primary" fontWeight="bold">
                    ${product.price}
                  </Typography>
                  {product.compare_at_price && product.compare_at_price > product.price && (
                    <Typography
                      variant="h6"
                      color="text.secondary"
                      sx={{ textDecoration: 'line-through' }}
                    >
                      ${product.compare_at_price}
                    </Typography>
                  )}
                </Box>
                {product.sku && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    SKU: {product.sku}
                  </Typography>
                )}
              </Box>

              <Divider sx={{ my: 3 }} />

              <Typography variant="body1" paragraph>
                {product.description || 'No description available.'}
              </Typography>

              {product.stock_quantity > 0 && (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                    <Typography variant="body1">Quantity:</Typography>
                    <TextField
                      type="number"
                      value={quantity}
                      onChange={(e) => setQuantity(Math.max(1, Math.min(product.stock_quantity, parseInt(e.target.value) || 1)))}
                      inputProps={{ min: 1, max: product.stock_quantity }}
                      sx={{ width: 100 }}
                    />
                  </Box>

                  <Button
                    variant="contained"
                    size="large"
                    startIcon={<ShoppingCart />}
                    onClick={handleAddToCart}
                    fullWidth
                    sx={{ mb: 2 }}
                  >
                    Add to Cart
                  </Button>
                </>
              )}

              {product.stock_quantity <= 5 && product.stock_quantity > 0 && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  Only {product.stock_quantity} left in stock - order soon!
                </Alert>
              )}
            </Box>
          </Grid>
        </Grid>

        {/* Reviews Section */}
        <Box sx={{ mt: 6 }}>
          <Typography variant="h5" gutterBottom fontWeight="bold">
            Customer Reviews
          </Typography>

          {/* Add Review Form */}
          {isAuthenticated && (
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Write a Review
              </Typography>
              <Box component="form" onSubmit={handleSubmitReview}>
                <Box sx={{ mb: 2 }}>
                  <Typography component="legend">Rating</Typography>
                  <Rating
                    value={review.rating}
                    onChange={(e, newValue) => setReview({ ...review, rating: newValue })}
                  />
                </Box>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Your Review"
                  value={review.comment}
                  onChange={(e) => setReview({ ...review, comment: e.target.value })}
                  sx={{ mb: 2 }}
                />
                <Button type="submit" variant="contained">
                  Submit Review
                </Button>
              </Box>
            </Paper>
          )}

          {/* Reviews List */}
          {product.reviews && product.reviews.length > 0 ? (
            product.reviews.map((review) => (
              <Paper key={review.id} sx={{ p: 3, mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {review.user_name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {new Date(review.created_at).toLocaleDateString()}
                  </Typography>
                </Box>
                <Rating value={review.rating} readOnly size="small" sx={{ mb: 1 }} />
                <Typography variant="body2">{review.comment}</Typography>
              </Paper>
            ))
          ) : (
            <Typography variant="body2" color="text.secondary">
              No reviews yet. Be the first to review this product!
            </Typography>
          )}
        </Box>
      </Container>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ProductDetail;
